'use client'

import { Item } from '@/app/api/types/redpim.types'
import DatoLink from '@/components/DatoLink/DatoLink'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageRecord, ProductDetailRecord } from '@/graphql/types'
import { useUnitContext } from '@/hooks/useUnitContext'
import { getInlineSpecsByUnit } from '@/utils/getSpecsFromItemSpecificationBlocs'
import { Button, cn, SelectButton } from '@desoutter/uikit'
import { FileText } from 'lucide-react'
import Image from 'next/image'
import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'
import { transformLocale } from '@/utils/language'

type ProductDetailsDescriptionProps = {
	lang: string
	item: Item
	items?: Item[]
	fragment: ProductDetailRecord
	translations: LanguageRecord
	resourcesExists: boolean
	sparePartsExists: boolean
	accessoriesExists: boolean
}

export default function ProductDetailsSticky({
	lang,
	item,
	items,
	fragment,
	translations,
	resourcesExists,
	sparePartsExists,
	accessoriesExists,
}: ProductDetailsDescriptionProps) {
	const selectItem = items?.find((el) => el.partnumber === item.partnumber)
	const { unit } = useUnitContext()
	const brand = process.env.NEXT_PUBLIC_PROJECT

	// Helper function to calculate button colspan
	const totalButtons = fragment.buttons.length
	const getButtonColspan = (buttonIndex: number): string => {
		if (resourcesExists) {
			// With resources button: first fragment button takes 50% in first row
			if (totalButtons === 2 && buttonIndex === 1) {
				// Second button takes full width in second row
				return 'col-span-2'
			}
			// All other cases: default 50% width
			return ''
		} else {
			// Without resources button: original logic
			if (totalButtons === 1 || (totalButtons === 3 && buttonIndex === 2)) {
				return 'col-span-2'
			}
			return ''
		}
	}

	// Formatting resources section anchor like in ProductDetailsResources.tsx
	const resourcesSectionId =
		'spy_' + translations.resources.replaceAll(' ', '_')

	const gotoProductResources = () => {
		const resourcesSection = document.querySelector(`#${resourcesSectionId}`)
		if (resourcesSection) {
			resourcesSection.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}

	const language = transformLocale({
		locale: lang,
		to: 'dato',
	})

	return (
		<div
			className={cn(
				'container-auto xl:sticky flex justify-end top-4 xl:top-48 right-0 xl:h-0 z-30 xl:mb-[680px]',
				{
					'xl:mb-[1080px]': sparePartsExists || accessoriesExists,
					'xl:mb-[1340px]': sparePartsExists && accessoriesExists,
				},
			)}
		>
			{selectItem && (
				<div className="w-full xl:w-[500px]">
					<div
						className="bg-white rounded-2xl p-4 lg:p-8"
						style={{
							boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.08)',
						}}
					>
						<div className="h4">{item?.model}</div>
						{item?.shortDescriptionsIntl && (
							<p>{item?.shortDescriptionsIntl[language]}</p>
						)}
						{items?.length > 1 && (
							<SelectButton
								className="mb-6"
								selectedElem={
									<LocalizedLink
										locale={lang}
										href={`/products/${selectItem.partnumber}`}
										className="flex gap-1"
										rel="nofollow"
									>
										{selectItem.thumbnail ? (
											<Image
												src={
													selectItem.thumbnail
														? new URL(selectItem.thumbnail).toString() +
															'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
														: '/images/image-placeholder.svg'
												}
												width="40"
												height="40"
												className="object-contain"
												alt=""
											/>
										) : (
											<Image
												src="/images/image-placeholder.svg"
												width="40"
												height="40"
												alt={selectItem.model}
												style={{ borderRadius: '8px' }}
											/>
										)}
										<div className="ml-2 pr-6 overflow-auto">
											<p className="text-md font-bold truncate">
												{selectItem.model}
											</p>
											<p>
												{selectItem.specificationBlocks
													? getInlineSpecsByUnit(
															selectItem.specificationBlocks,
															unit,
														)
													: selectItem.partnumber}
											</p>
										</div>
									</LocalizedLink>
								}
							>
								{items?.map((el, i) => (
									<li
										className="border-b last-of-type:border-b-0"
										key={`${el.partnumber}-${i}`}
									>
										<LocalizedLink
											locale={lang}
											prefetch={false}
											href={`/products/${el.partnumber}`}
											className="flex w-full pr-4 pb-2 items-center gap-1"
											rel="nofollow"
										>
											{!!el.thumbnail ? (
												<Image
													src={
														el.thumbnail
															? new URL(
																	el.thumbnail +
																		'/jcr:content/renditions/cq5dam.web.400.400.jpeg',
																).toString()
															: '/images/image-placeholder.svg'
													}
													width="40"
													height="40"
													alt={el.model}
												/>
											) : (
												<Image
													src="/images/image-placeholder.svg"
													width="40"
													height="40"
													alt={el.model}
													style={{ borderRadius: '8px' }}
												/>
											)}
											<div className="ml-2">
												<p className="w-80 text-md font-bold truncate">
													{el.model}
												</p>
												<p>
													{el.specificationBlocks
														? getInlineSpecsByUnit(el.specificationBlocks, unit)
														: el.partnumber}
												</p>
											</div>
										</LocalizedLink>
									</li>
								))}
							</SelectButton>
						)}
						<div className="pt-6 border-t border-gray-100">
							<DatoLink
								fragment={fragment.redButton as DatoLinkFragment}
								params={`?pn=${item.partnumber}`}
								className="productStickyQuote"
							>
								<Button className="md w-full justify-center" noIcon>
									<FileText
										className={
											brand === 'rodcraft' ? 'text-gray-900' : 'text-white'
										}
									/>
									{fragment.redButton.label}
								</Button>
							</DatoLink>
							<div className="grid grid-cols-2 gap-2 mt-4">
								{resourcesExists && (
									<Button
										variant="outline"
										color="tertiary"
										noIcon
										onClick={() => gotoProductResources()}
										className={cn(
											'border-gray-200',
											// If no fragment buttons, make resources button span full width
											fragment.buttons.length === 0 ? 'col-span-2' : '',
										)}
									>
										{translations.productResources}
									</Button>
								)}
								{fragment.buttons.map((button, i) => {
									const buttonColspan = getButtonColspan(i)
									return (
										<DatoLink
											fragment={button as DatoLinkFragment}
											params={
												`?${
													button.__typename === 'InternalLinkRecord'
														? 'pn'
														: 'search'
												}=` + item.partnumber
											}
											className={cn(
												buttonColspan,
												i ? 'productStickyBuy' : 'productStickyQuestion',
											)}
											key={button.id}
										>
											<Button
												variant="outline"
												color="tertiary"
												title={button.label}
												noIcon
												className="border-gray-200 w-full"
											>
												{button.label}
											</Button>
										</DatoLink>
									)
								})}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
